import {useState, useEffect } from 'react';
import { useFormik, FormikProvider } from 'formik';
import { axiosInstance } from "../../components/helpers/axiosInstance";
import TitleTooltip from "../../components/helpers/TitleTooltip";
import PagoCaja from "./componentes/PagoCaja";
import EstadosSelect from "../../components/catalogo/EstadosSelect";
import { useToast } from "../../components/notificaciones/ToastProvider";
import {validacionBusqueda} from "./validaciones/validacionesCorte";
import ErrorMessage from "../../components/helpers/ErrorMessage";
import dayjs from "dayjs";
import es from "dayjs/locale/es"
import TablaCorte from "../../components/helpers/TablaCorte";
import CardCorte from "../../components/helpers/CardCorte";
import { CajasCompletas } from "../../components/catalogo/Cajas"
import Conceptos from "../../components/catalogo/Conceptos";
import TipoConsulta from "../../components/catalogo/TipoConsulta";

/**
 * COMPONENTE PRINCIPAL
 * @returns {JSX.Element}
 * @constructor
 */
export default function BusquedasRealizadas() {
    /**
     * Guardar datos para la tabla
     */
    const [busquedas, setBusquedas] = useState([]);
    /**
     * Fecha del día actual ( valor inicial de DatePicker )
     * @type {Date}
     */
    const date = new Date();
    /**
     * Notificaciones
     * @type {undefined}
     * @property setArrayToast
     */
    const  toastP = useToast();

    /**
     * @function onSubmit enviar y registar movimiento
     * @param values
     */
    function onSubmit(values){
            axiosInstance.post(
                '/cajas/' + values.id_caja + '/movimientos',
                {
                    movimiento: "I",
                    cantidad: values.cantidad,
                    monto: values.costo,
                    factura: "NO",
                    forma_pago: values.forma_pago,
                    concepto: values.concepto,
                    tipo_consulta: values.tipo_consulta,
                }
            ).then(function (response) {

                /**
                 * Sí se hizo correctamente la petición se agrega la búsqueda al
                 * arreglo "busquedas" para mostralo en la tabla, se muestra la notificación
                 * de exito y se reinician los valores del formulario.
                 */
                setBusquedas([...busquedas, response.data]);
                toastP.setArrayToast(
                    prevToasts => [
                        ...prevToasts,
                        {
                            id : response.data.id,
                            text: "Búsqueda registrada",
                            color: 'success',
                            show: true
                        }
                    ]
                );
                formik.resetForm();

            }).catch(function (error) {
                /**
                 * Cuando no se hace el registro se muestra la notificación de error
                 * y se muestra el error en el input correspondiente
                 */
                toastP.setArrayToast(
                    prevToasts => [
                        ...prevToasts,
                        {
                            id : 1,
                            text: error.response.data.errors[0].message,
                            color: 'danger',
                            show: true
                        }
                    ]
                );

                formik.setFieldError(`${error.response.data.errors[0].input}`, `${error.response.data.errors[0].details}`)

            });
    }

    /**
     * Valores iniciales de formulario
     * @type {{fecha: Date, forma_pago: string, id_caja: number, estado: string, concepto: string, tipo_consulta: string, cantidad: string, costo: string, foranea: boolean}}
     */
    const valorInicial = {
        fecha: date,
        forma_pago: "TR",
        id_caja: 1,
        estado: "CMX",
        concepto: "TT",
        tipo_consulta: "Estatal",
        cantidad: "",
        costo: "",
        foranea: false,
    }

    /**
     * Constantes de formik para manejar el formulario
     */
    const formik = useFormik({
        initialValues : valorInicial,
        validationSchema : validacionBusqueda,
        validateOnMount : true,
        onSubmit,
    });

    /**
     * Desestructuracion del objeto formik
     */
    const { values, setFieldValue } = formik;

    /**
     * Calcular el costo de la búsqueda según el estado y el tipo
     */
    useEffect(() => {

        if(values.estado !== "CMX" && values.estado !== "MEX"){
            values.concepto = "TT";
        }

        const consultaFederal = values.tipo_consulta !== "Federal y Estatal";

        switch (values.concepto){
            case "1A":
                consultaFederal ? values.costo = 40 * Number(values.cantidad) : values.costo = 60 * Number(values.cantidad);
                break;
            case  "4A":
                consultaFederal ? values.costo = 50 * Number(values.cantidad) : values.costo = 75 * Number(values.cantidad);
                break;
            case "6A":
                consultaFederal ? values.costo = 60 * Number(values.cantidad) : values.costo = 90 * Number(values.cantidad);
                break;
            case "10A":
                consultaFederal ? values.costo = 80 * Number(values.cantidad) : values.costo = 120 * Number(values.cantidad);
                break;
            default :
                if(values.estado === "CMX" || values.estado === "MEX"){
                    consultaFederal ? values.costo = 100 * Number(values.cantidad) : values.costo = 150 * Number(values.cantidad);
                } else {
                    values.foranea === true ? values.costo = 100 * Number(values.cantidad) : values.costo = 60 * Number(values.cantidad);
                }
                break;
        }
    },[values]);
    /**
     * Sí la forma de pago es "TERMINAL" se asigna la caja 1 a id_caja,
     * síla forma de pago es "CONVENIENCIA" se asigna la caja 2 a id_caja
     */
    useEffect(() => {
        if(values.forma_pago === "TE"){
            setFieldValue('id_caja', 1);
        } else if(values.forma_pago === "CO"){
            setFieldValue("id_caja", 2)
        }
    }, [values.forma_pago, setFieldValue]);

    return (
        <>
            <style type={'text/css'}>
                {`
                    .rs-picker-has-value .rs-btn .rs-picker-toggle-value, .rs-picker-has-value .rs-picker-toggle .rs-picker-toggle-value{
                        color : var(--bs-gray-700)!important;
                    }
                `}
            </style>
            <CardCorte>
                <div className="card card-bordered d-flex flex-row-fluid flex-center">
                    <FormikProvider value={formik}>
                    <form className="card-body py-15 w-100 px-10" onSubmit={formik.handleSubmit}>
                        {
                            /**
                             * INICIA : FORMULARIO DE BÚSQUEDAS
                             */
                        }
                        <div className={'row'}>
                            <PagoCaja formik={formik} mov={false}></PagoCaja>
                            <div className={'col-md-3 mb-7'}>
                                <EstadosSelect formik={formik}></EstadosSelect>
                            </div>
                        </div>
                        <div className="row justify-content-end ">
                            {
                                (values.estado === "CMX" || values.estado === "MEX") && <>
                                    <div className={"col-md-3 mb-7"}>
                                        <Conceptos formik={formik}></Conceptos>
                                    </div>
                                    <div className={"col-md-3 mb-7"}>
                                        <TipoConsulta formik={formik}></TipoConsulta>
                                    </div>
                                </>
                            }
                            <div className={"col-md-3"}>
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        className="form-control form-control-solid"
                                        id="cantidad"
                                        name="cantidad"
                                        value={values.cantidad}
                                        placeholder="Total de búsquedas"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <label htmlFor="cantidad" className={"text-gray-700"}>Cantidad</label>
                                </div>
                                <ErrorMessage name={'cantidad'}></ErrorMessage>
                            </div>
                            <div className={'col-md-3 mb-7'}>
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        className="form-control bg-gray-200 text-gray-700 border-0"
                                        id="costo"
                                        name="costo"
                                        value={values.costo}
                                        placeholder="Costo total"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        disabled={true}
                                    />
                                    <label htmlFor="costo" className={"text-gray-700"}>Costo total</label>
                                </div>
                            </div>
                            {
                                (values.estado !== "CMX" && values.estado !== "MEX") &&
                                <div className={'col-md-3 align-content-end mb-7'}>
                                    <div className="d-flex flex-stack">
                                        <div
                                            className="form-check form-switch form-check-reverse d-flex align-items-center">
                                            <label htmlFor={'foranea-switch'}
                                                   className="fw-bold text-gray-700 fs-5 text-end me-2">Búsqueda
                                                foránea</label>
                                            <input id={'foranea-switch'} className="form-check-input w-30px h-15px m-0"
                                                   type="checkbox" checked={values.foranea}
                                                   onChange={(e) => setFieldValue('foranea', e.target.checked)}/>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                        <div className={'d-flex justify-content-end mt-2'}>
                            <button className="btn btn-primary me-3 " type="submit"
                                    disabled={Object.keys(formik.errors).length > 0}>Enviar
                            </button>
                        </div>
                        {
                            /**
                             * FIN : FORMULARIO DE BÚSQUEDAS
                             */
                        }
                    </form>
                </FormikProvider>
                </div>
                <div className="card card-bordered d-flex flex-row-fluid flex-center mt-5">
                    {
                        /**
                         * INICIA : CONSULTAR BÚSQUEDAS
                         */
                    }
                    <TablaBusquedas busquedas={busquedas} setBusquedas={setBusquedas}></TablaBusquedas>
                    {
                        /**
                         * FIN : CONSULTAR BÚSQUEDAS
                         */
                    }
                </div>
            </CardCorte>
        </>
    );
}

//////////////// FIN : COMPONENTE PRINCIPAL //////////////////

function TablaBusquedas({busquedas, setBusquedas }) {
    /**
     * Cambiear lenguaje de libreria dayjs
     */
    dayjs.locale(es);
    /**
     * Notificaciones
     * @type {undefined}
     * @property setArrayToast
     */
    const toast = useToast();
    /**
     * @function onSubmit no es necesaria pero causa un warning si no se pone
     * @param values
     */
    const onSubmit = (values) => { console.log(values); }
    /**
     * @const formik manejo del formulario
     */
    const formik = useFormik({
        initialValues: {
            caja_tabla: 100,
        },
        onSubmit,
    });
    /**
     * constante de valores del formulario
     */
    const { values } = formik;
    /**
     * Se hace la petición cada vez que se cambia la caja
     */
    useEffect(() => {
        axiosInstance.get("/cajas/" + values.caja_tabla + "/movimientos?movimientos=ingresos,egresos&conceptos=consultas")
        .then((res) =>{
            setBusquedas(res.data);
        }).catch((error) => console.log(error));
    },[values, setBusquedas]);

    /**
     * @function handleDeleteRow eliminar fila
     * @param rowItem
     */
    function handleDeleteRow(rowItem) {
        /**
         * Petición al servidor
         */
        axiosInstance.delete("/cajas/" + values.caja_tabla + "/movimientos/" + rowItem)
            .then((res) => {
                if (res.status === 204) {
                    /**
                     * Quitar dato de array y tabla
                     */
                    setBusquedas(busquedas.filter((c) => c.id !== rowItem));
                    /**
                     * Notificación : se elimino correctamente
                     */
                    toast.setArrayToast(
                        prevState => [
                            ...prevState,
                            {
                                id: rowItem,
                                text: "Movimiento eliminado",
                                color: "success",
                                show: true,
                            }
                        ]
                    );
                }
            }).catch((error) => {
            /**
             * Notificación : Sí la respuesta tiene un código 404 sigfica que el usuario no tene acceso
             */
            if (error.status === 404) {
                    toast.setArrayToast(prevState => [
                        ...prevState,
                        {
                            id: rowItem,
                            text: "Usuario sin acceso, el administrador del sistema sera notificado",
                            color: "danger",
                            show: true,
                        }
                    ])
                /**
                 * Notificación : Sí es otra cosa muestra el mensaje
                 */
                } else {
                    toast.setArrayToast(prevState => [
                        ...prevState,
                        {
                            id: rowItem,
                            text: error.message,
                            color: "danger",
                            show: true,
                        }
                    ])
                }
        });
    }

    /**
     * @function nombreConsulta Cambiar nombre de consulta a cadena
     * @param concepto
     * @returns {{name: string, color: string}}
     */
    function nombreConsulta(concepto){
        switch(concepto) {
            case "1A":
                return { name : "1 año", color : "primary"};
            case "4A":
                return {name : "4 años", color : "warning"};
            case "6A":
                return {name : "6 años", color: "danger"};
            case "10A":
                return {name : "10 años", color: "info"};
            default:
                return {name : "Total", color: "success"};
        }
    }

    return (
      <div className={"card-body py-15 w-100 px-10"}>
          {
              /**
               * INICIA : SELECT DE CAJA
               */
          }
          <div className='row mb-2'>
              <div className={'col-md-2'}>
                  <CajasCompletas formik={formik}></CajasCompletas>
              </div>
          </div>
          {
              /**
               * FIN : SELECT DE CAJA
               */
          }
          <TablaCorte data={busquedas}>
              <thead>
                <tr className={'fw-bold text-start text-muted pb-5 text-uppercase'}>
                    <th className={'ps-2'}>Fecha</th>
                    <th>Capturo</th>
                    <th>Cantidad</th>
                    <th>Concepto</th>
                    <th>Forma de pago</th>
                    <th>Monto</th>
                </tr>
              </thead>
            <tbody>
            {
                /**
                 * INICIA : FILAS DEL REGISTRO DE BÚSQUEDAS
                 */
            }
            {
                busquedas && busquedas.toReversed().map((i) => {
                    return (
                        <tr key={i.id}  className='text-uppercase text-gray-900 fw-semibold text-start'>
                            <td className={'align-content-center ps-2'}>{dayjs(i.fecha).format('MMM DD, YYYY')}</td>
                            <td className={'align-content-center'}>{i.capturo}</td>
                            <td className={'align-content-center'}>{i.cantidad}</td>
                            <td className={'align-content-center'}>
                                <div className={'symbol symbol-2by3'}>
                                    <span className={"symbol-label h-25px bg-light-" + nombreConsulta(i.concepto).color}>{nombreConsulta(i.concepto).name}</span>
                                </div>
                            </td>
                            <td className={'align-content-center'}>{nombreTipoPago(i.forma_pago)}</td>
                            <td className={'align-content-center'}>
                                <span className={"fw-bold fs-6"}>
                                    {i.monto}
                                </span>
                            </td>
                            {
                                dayjs(i.fecha).format('MMM DD, YYYY') === dayjs().format('MMM DD, YYYY') && <td className={'d-flex justify-content-end'}>
                                    <TitleTooltip title="Borrar" placement={'top'}>
                                        <button type={'button'} onClick={() => handleDeleteRow(i.id)} className="btn btn-light-danger btn-icon btn-sm">
                                            <i className="ki-solid ki-trash fs-5"></i>
                                        </button>
                                    </TitleTooltip>
                                </td>
                            }
                        </tr>
                    );
                })
            }
            {
                /**
                 * FIN : FILAS DEL REGISTRO DE BÚSQUEDAS
                 */
            }
            </tbody>
        </TablaCorte>
      </div>
    );
  }

export function nombreTipoPago(val){
    switch(val) {
        case "TR":
            return "Transferencia";
        case "DE":
            return "Deposito";
        case "EF":
            return "Efectivo";
        case "CO":
            return "Conveniencia";
        case "TE":
            return "Terminal";
        default:
            return "Cheque";
    }
}

